import React from 'react';
import NavigationPrompt from "react-router-navigation-prompt";
import styled from 'styled-components';

// Material UI
import Box from '@material-ui/core/Box';
import SignOutIcon from '@material-ui/icons/ExitToApp';
import PlayersIcon from '@material-ui/icons/Group';
import { JerseyIcon } from '../../../Session/CheckIn/check-in.helpers';
import DashboardIcon from '@material-ui/icons/Dashboard';
import DrillsIcon from '@material-ui/icons/Assignment';
import SessionsIcon from '@material-ui/icons/DateRange';
import UpcomingIcon from '@material-ui/icons/Check';
import MyAssignmentsIcon from '@material-ui/icons/AssignmentInd';
import ArrowLeftIcon from '@material-ui/icons/KeyboardArrowLeft';
import ArrowRightIcon from '@material-ui/icons/KeyboardArrowRight';
import GearIcon from '@material-ui/icons/Settings';
import SettingsApplicationsIcon from '@material-ui/icons/SettingsApplications';
import EmailIcon from '@material-ui/icons/Email';
import SupervisorAccountIcon from '@material-ui/icons/SupervisorAccount';

// Data
import { SPORT_TERMINOLOGY } from '../../../../lib/constants';
import logoIcon from '../../../../assets/logo.png'
import meaLogo from '../../../../assets/logo.png';
import jersey from '../../../../assets/inverse-jersey-wrapper.png';

// Components
import { CollapsibleNavItem } from '../../../_core/_ui/nav.components';
import { Spacer } from '../../../_core/_ui/structure.components';
import { Button, Fab, Fade, Modal, Paper } from '@material-ui/core';
import withStyles from '@material-ui/core/styles/withStyles';
import { mainColor } from '../../../../lib/theme';
import { useHistory } from 'react-router-dom';
import { buildSignOut } from '../../../../lib/services/auth.service';
import { SessionType } from '../../../../lib/types/session.types';
import CompanySelectorGraphQL from '../../../Company/CompanySelector/company-selector.graphql';
import { pluralTerm } from '../../../../lib/helpers/term.helper';
import { hasAdminAccess, hasAnyRoles } from '../../../../lib/services/user.service';
import { hasCommunicationAccess } from '../../../../lib/services/communication.service';
import {
  openCommunicationPanel,
  useIsCommunicationPanelOpen
} from '../../../../lib/stores/communication.store';
import {
  useIsNavCollapsed,
  setIsNavCollapsed,
  setIsContextCollapsed
} from '../../../../lib/stores/dashboard-layout.store';
import { CommunicationPanel } from '../../../Communication/CommunicationPanel/communication-panel.component';
import { getActiveCompanyId, getCompany } from '../../../../lib/services/company.service';
import { useCompanyHasAutomation, useCompanyHasCalibrationVideos, useCompanySportTerminology } from '../../../../lib/stores/company.store';
import { useHasUnsavedFormat, setHasUnsavedFormat } from '../../../../lib/stores/assessment-stage.store';
import { Sport, SportTerminology } from '../../../../lib/types';
import { ROLES, STAFF_PATHS } from '../../../../../_configuration';
import { getActiveAssessmentId } from '../../../../lib/services/assessment.service';
import { usePendingCalibrationCount } from '../../../../lib/stores/calibration-assignment.store';
import { Label } from '../../../_core/_ui/typography.component';
import { IntlMsg } from '../../../_core/IntlMsg/intl-msg.component';
import { messages } from './dashboard-nav.messages';

const Logo = styled.img`
  width: 136px;
`;

const ToggleFab = withStyles(() => ({
  root: {
    boxShadow: '0px 2px 1px -1px rgba(0,0,0,0.2),0px 1px 1px 0px rgba(0,0,0,0.14),0px 1px 3px 0px rgba(0,0,0,0.12)',
    position: 'absolute',
    right: '-12px',
    top: '35px',
    color: '#fff',
    background: mainColor,
    width: '24px',
    height: '24px',
    minHeight: 'auto'
  }
}))(Fab);

interface IWrapper {
  isCollapsed?: boolean;
}
const Wrapper = styled.div<IWrapper>`
  display: flex;
  flex-shrink: 0;
  flex-direction: column;
  padding: 8px 0;
  position: fixed;
  height: 100%;
  width: ${props => props.isCollapsed ? 'auto' : '190px'};
  overflow-y: auto;
`;

const modalStyle = {
  position: 'absolute' as 'absolute',
  top: '50%',
  left: '50%',
  transform: 'translate(-50%, -50%)',
  width: 650,
  bgcolor: 'background.paper',
  border: '2px solid #000',
  boxShadow: 24,
  p: 4,
};

export const DashboardNav = () => {
  const hasUnsavedFormat = useHasUnsavedFormat();
  const history = useHistory();
  const signOut = buildSignOut();
  const isCollapsed = useIsNavCollapsed();
  const hasAutomation = useCompanyHasAutomation();
  const termLookup = useCompanySportTerminology()
  const defaultTermLookup = SPORT_TERMINOLOGY[Sport.default]
  const athletesTerm = '/' + pluralTerm(defaultTermLookup[SportTerminology.ATHLETE]).toLowerCase()
  const eventsTerm = '/' + pluralTerm(defaultTermLookup[SportTerminology.EVENT]).toLowerCase()

  const [isScrollable, setIsScrollable] = React.useState(false);

  React.useEffect(() => {
    const checkScrollable = () => {
      const wrapper = document.querySelector('[data-nav-wrapper="true"]');
      if (wrapper) {
        setIsScrollable(wrapper.scrollHeight > wrapper.clientHeight);
      }
    };

    checkScrollable();
    window.addEventListener('resize', checkScrollable);
    return () => window.removeEventListener('resize', checkScrollable);
  }, []);

  const toggleCollapsed = () => {
    if (isCollapsed) {
      setIsContextCollapsed(false);
    }

    setIsNavCollapsed(!isCollapsed);
  }

  const assessmentId = getActiveAssessmentId();
  const companyId = getActiveCompanyId();
  const { company } = getCompany(companyId);

  const displayLogo = company?.logo ?? meaLogo;

  // Call hooks at top level to avoid conditional hook calls
  const hasCheckInOrScoring = hasAnyRoles({roles: [ROLES.CHECK_IN, ROLES.SCORING], assessmentId});
  const hasScoring = hasAnyRoles({roles: [ROLES.SCORING], assessmentId});
  // Assignments is a company opt-in feature, so gate the launcher on it too (the
  // page itself re-checks via hasCalibrationAssignmentAccess).
  const hasCalibrationVideos = useCompanyHasCalibrationVideos();
  // Pending-count badge on the Assignments launcher; kept fresh by CalibrationAssignmentsPoller.
  const pendingCalibrationCount = usePendingCalibrationCount();
  // Nav doesn't need to distinguish loading from no-permission — both produce
  // "don't render the item," so we destructure only `hasCommunication`.
  const { hasCommunication } = hasCommunicationAccess();
  const isCommunicationPanelOpen = useIsCommunicationPanelOpen();
  // Global Max admin tools (e.g. create association) — gated on the User-level
  // isAdmin flag, not a company role. See hasAdminAccess.
  const { isAdmin } = hasAdminAccess();


  return (
    <>
      <NavigationPrompt afterConfirm={() => setHasUnsavedFormat(false)} when={hasUnsavedFormat}>
        {({ isActive, onCancel, onConfirm }) => {
          if (isActive) {
            return (
              <Modal open closeAfterTransition>
                <Paper>
                <Fade in>
                  <Box css={modalStyle}>
                    <Label style={{ fontWeight: 'bold' }}><IntlMsg {...messages.unsavedTitle} /></Label>
                    <Button onClick={onCancel}  variant='contained' style={{ marginRight: '0.5rem' }}><IntlMsg {...messages.unsavedNo} /></Button>
                    <Button onClick={onConfirm} variant='contained' color='primary' style={{ color: 'white' }}><IntlMsg {...messages.unsavedYes} /></Button>
                  </Box>
                </Fade>
                </Paper>
              </Modal>
            );
          }
          return null;
        }}
      </NavigationPrompt>
      <ToggleFab style={{ position: 'fixed', zIndex: 101, left: isCollapsed ? '52px' : (isScrollable ? '178px' : '167px') }} onClick={() => toggleCollapsed()}>
        {isCollapsed
          ? <ArrowRightIcon style={{ position: 'relative', left: '1px', top: '1px' }} />
          : <ArrowLeftIcon style={{ position: 'relative', top: '1px' }} />
        }
      </ToggleFab>
      <Wrapper isCollapsed={isCollapsed} data-nav-wrapper="true">
        <Box style={{ marginTop: isCollapsed ? '8px' : '0' }} />
        <CollapsibleNavItem icon={isCollapsed && <Logo src={logoIcon} />} light isCollapsed={isCollapsed}>
          <Logo src={displayLogo} />
        </CollapsibleNavItem>

        {!isCollapsed && <Box p={2}><CompanySelectorGraphQL /></Box>}

        {/* 1. Settings */}
        {hasAnyRoles({roles: [ROLES.CONFIGURATION]}) && <>
          <CollapsibleNavItem
            isActive={history.location.pathname === '/settings'}
            isCollapsed={isCollapsed}
            icon={<SettingsApplicationsIcon />}
            onClick={() => history.push('/settings')}
          >
            <IntlMsg {...messages.settings} />
          </CollapsibleNavItem>
          <Spacer />
        </>}

        {/* Dashboard */}
        {hasAnyRoles({roles: [ROLES.REPORTS, ROLES.CONFIGURATION], anyAgeGroup: true}) && <CollapsibleNavItem
          isActive={history.location.pathname === '/'}
          isCollapsed={isCollapsed}
          icon={<DashboardIcon />}
          onClick={() => history.push('/')}
        >
          <IntlMsg {...messages.dashboard} />
        </CollapsibleNavItem>}

        {/* 2. Users */}
        {hasAnyRoles({roles: [ROLES.USER_MANAGEMENT], anyAgeGroup: true}) && <CollapsibleNavItem
          isActive={history.location.pathname === '/users'}
          isCollapsed={isCollapsed}
          icon={<PlayersIcon />}
          onClick={() => history.push('/users')}
        >
          <IntlMsg {...messages.users} />
        </CollapsibleNavItem>}

        {/* 3. Drills */}
        {hasAnyRoles({roles: [ROLES.CONFIG_DRILLS]}) && <CollapsibleNavItem
          isActive={history.location.pathname === eventsTerm || history.location.pathname === '/practice-plans' || history.location.pathname === '/' + defaultTermLookup[SportTerminology.METRIC].toLowerCase()}
          isCollapsed={isCollapsed}
          icon={<DrillsIcon />}
          onClick={() => history.push(eventsTerm)}
        >
          {pluralTerm(termLookup[SportTerminology.EVENT])}
        </CollapsibleNavItem>}

        {/* 4. Format */}
        {hasAnyRoles({roles: [ROLES.CONFIGURATION], anyAgeGroup: true}) && hasAutomation && <CollapsibleNavItem
          isActive={history.location.pathname === '/format'}
          isCollapsed={isCollapsed}
          icon={<GearIcon />}
          onClick={() => history.push('/format')}
        >
          <IntlMsg {...messages.format} />
        </CollapsibleNavItem>}

        {/* 5. Ice Times (Sessions) */}
        {hasAnyRoles({roles: [ROLES.CONFIGURATION], anyAgeGroup: true}) && <CollapsibleNavItem
          isActive={history.location.pathname === '/sessions'}
          isCollapsed={isCollapsed}
          icon={<SessionsIcon />}
          onClick={() => history.push('/sessions')}
        >
          {pluralTerm(termLookup[SportTerminology.SESSION])}
        </CollapsibleNavItem>}

        {/* 6. Players */}
        {hasAnyRoles({roles: [ROLES.CONFIGURATION, ROLES.REPORTS], anyAgeGroup: true}) && <CollapsibleNavItem
          isActive={history.location.pathname === athletesTerm}
          isCollapsed={isCollapsed}
          icon={
            <JerseyIcon
              style={{
                width: '18px',
                height: '18px',
                padding: '3px 0',
                fontSize: '8px',
                fontWeight: 'bold',
                textAlign: 'center',
                flexShrink: 0,
                backgroundColor: 'transparent'
              }}
              url={jersey} />
          }
          onClick={() => history.push(athletesTerm)}
        >
          {pluralTerm(termLookup[SportTerminology.ATHLETE])}
        </CollapsibleNavItem>}

        {/* 8 & 9. Check-in / Evaluate - Show different text based on permissions */}
        {hasCheckInOrScoring && <CollapsibleNavItem
          isActive={history.location.pathname === '/upcoming'}
          isCollapsed={isCollapsed}
          icon={<UpcomingIcon />}
          onClick={() => history.push('/upcoming')}
        >
          {hasScoring ? <IntlMsg {...messages.evaluate} /> : <IntlMsg {...messages.checkIn} />}
        </CollapsibleNavItem>}

        {/* Assignments — the evaluator's calibration assignments (company opt-in) */}
        {hasScoring && hasCalibrationVideos && <CollapsibleNavItem
          isActive={history.location.pathname === STAFF_PATHS.ASSIGNMENTS}
          isCollapsed={isCollapsed}
          icon={<MyAssignmentsIcon />}
          badgeContent={pendingCalibrationCount}
          onClick={() => history.push(STAFF_PATHS.ASSIGNMENTS)}
        >
          <span style={{fontSize: 13}}><IntlMsg {...messages.assignments} /></span>
        </CollapsibleNavItem>}

        {/* 10. Communication — opens the global side panel rather than routing
            to a dedicated page. The panel is the primary compose surface and
            stays accessible from any route for users with the role. */}
        {hasCommunication && <CollapsibleNavItem
          isActive={isCommunicationPanelOpen}
          isCollapsed={isCollapsed}
          icon={<EmailIcon />}
          onClick={openCommunicationPanel}
        >
          <span style={{fontSize: 13}}><IntlMsg {...messages.communication} /></span>
        </CollapsibleNavItem>}

        {/* Admin — global Max admin only (isAdmin), not a company role */}
        {isAdmin && <CollapsibleNavItem
          isActive={history.location.pathname === '/admin'}
          isCollapsed={isCollapsed}
          icon={<SupervisorAccountIcon />}
          onClick={() => history.push('/admin')}
        >
          <IntlMsg {...messages.admin} />
        </CollapsibleNavItem>}

        <Spacer />

        {/* Sign Out */}
        <CollapsibleNavItem
          onClick={async () => await signOut(SessionType.STAFF)}
          isCollapsed={isCollapsed}
          icon={<SignOutIcon />}
        >
          <IntlMsg {...messages.signOut} />
        </CollapsibleNavItem>
        <CollapsibleNavItem icon={isCollapsed && <Logo src={logoIcon} />} light isCollapsed={isCollapsed}>
          <Logo src={meaLogo} />
        </CollapsibleNavItem>
      </Wrapper>
      {hasCommunication && <CommunicationPanel />}
    </>
  );
};
